/*
 * Sidequest visual navigation — scoped desktop dropdown behaviour.
 *
 * Layers pointer and focus intent on top of a native <details>/<summary>.
 * Dawn's <header-menu> handles click and focusout but has no hover-to-open,
 * which the approved design requires.
 *
 * Deliberate constraints:
 *   - global.js and details-disclosure.js are not modified or subclassed.
 *   - The summary's native open path is never cancelled, so with this file
 *     blocked or failed the panel is still a working click-to-open disclosure.
 *   - Element-scoped listeners only, so Theme Editor section reloads cannot
 *     accumulate handlers.
 */
(function () {
  'use strict';

  var OPEN_DELAY = 120; // intentional hover, not a pass-through
  var CLOSE_DELAY = 200; // covers the gap between summary and panel
  var DESKTOP_MIN = 990; // matches Dawn's inline-menu breakpoint

  if (customElements.get('nav-visual-menu')) return;

  class NavVisualMenu extends HTMLElement {
    connectedCallback() {
      /*
       * A custom element can be upgraded before its own children have been
       * parsed — that happens whenever the definition is already registered
       * as the markup streams in, and when the Theme Editor re-renders a
       * section. Retrying on the next frame is what makes initialisation
       * order-independent instead of silently doing nothing.
       */
      this.init();
    }

    init() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');

      if (!this.details || !this.summary) {
        if (this.initTries === undefined) this.initTries = 0;
        if (this.initTries < 5) {
          this.initTries += 1;
          var self = this;
          this.initFrame = requestAnimationFrame(function () {
            if (self.isConnected) self.init();
          });
        }
        return;
      }

      if (this.initialised) return;
      this.initialised = true;

      this.item = this.closest('li');
      this.openTimer = null;
      this.closeTimer = null;
      this.suppressHover = false;
      this.openedByHover = false;
      this.pointerFocus = false;

      this.onToggle = this.onToggle.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.setGap = this.setGap.bind(this);

      this.details.addEventListener('toggle', this.onToggle);
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.summary.addEventListener('click', this.onSummaryClick);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('resize', this.setGap);

      this.setGap();
    }

    disconnectedCallback() {
      this.clearTimers();
      if (this.initFrame) cancelAnimationFrame(this.initFrame);
      if (this.setGap) window.removeEventListener('resize', this.setGap);
    }

    clearTimers() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.openTimer = null;
      this.closeTimer = null;
    }

    /*
     * Anchor the panel to the header's bottom edge rather than the list
     * item's. `top: 100%` alone starts the panel inside the header's own
     * padding and border, so it overlaps the bar. Mirrors the intent of
     * Dawn's --header-bottom-position-desktop.
     */
    setGap() {
      var header = this.closest('.header');
      if (!header || !this.item) return;
      var gap = Math.round(header.getBoundingClientRect().bottom - this.item.getBoundingClientRect().bottom) + 8;
      this.item.style.setProperty('--nav-dd-gap', gap + 'px');
    }

    isDesktop() {
      return window.innerWidth >= DESKTOP_MIN;
    }

    hoverAllowed() {
      if (this.suppressHover) return false;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return false;
      return this.isDesktop();
    }

    onToggle() {
      var open = this.details.open;
      this.summary.setAttribute('aria-expanded', String(open));
      if (this.item) this.item.classList.toggle('nav-dd--open', open);
      if (open) this.setGap();
      else this.openedByHover = false;
    }

    onPointerEnter() {
      clearTimeout(this.closeTimer);
      if (!this.hoverAllowed() || this.details.open) return;
      var self = this;
      this.openTimer = setTimeout(function () {
        self.openedByHover = true;
        self.setGap();
        self.details.open = true;
      }, OPEN_DELAY);
    }

    onPointerLeave() {
      clearTimeout(this.openTimer);
      if (!this.hoverAllowed() || !this.details.open) return;
      var self = this;
      this.closeTimer = setTimeout(function () {
        if (!self.contains(document.activeElement)) {
          self.details.open = false;
          self.openedByHover = false;
        }
      }, CLOSE_DELAY);
    }

    /*
     * Touch and pen must not go through the hover path: on a hybrid laptop a
     * tap would otherwise open on hover and immediately close on click.
     */
    onPointerDown(event) {
      this.pointerFocus = true;
      var self = this;
      setTimeout(function () {
        self.pointerFocus = false;
      }, 0);

      if (event.pointerType !== 'mouse') {
        this.suppressHover = true;
        this.clearTimers();
        setTimeout(function () {
          self.suppressHover = false;
        }, 500);
      }
    }

    /*
     * A click while the panel is open from hover pins it open instead of
     * toggling it shut — otherwise pointing at a menu and clicking reads as
     * "my click closed the thing I just aimed at". A second click closes.
     */
    onSummaryClick(event) {
      if (this.details.open && this.openedByHover) {
        event.preventDefault();
        this.openedByHover = false;
      }
    }

    /*
     * Keyboard focus opens; pointer focus does not. mousedown focuses the
     * summary before the click lands, so auto-opening here would let the
     * click that follows toggle the panel straight back shut.
     */
    onFocusIn() {
      this.clearTimers();
      if (this.pointerFocus) return;
      if (!this.isDesktop()) return;
      if (!this.details.open) this.details.open = true;
    }

    onFocusOut(event) {
      if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
        this.details.open = false;
      }
    }

    onKeyDown(event) {
      if (event.key !== 'Escape' || !this.details.open) return;
      event.stopPropagation();
      this.details.open = false;
      this.summary.focus();
    }
  }

  customElements.define('nav-visual-menu', NavVisualMenu);
})();
